import React, {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  FormBox,
  SPWCancelButton,
  SPWMainButton,
} from 'spw-shared/globalComponents';
import { colors } from 'spw-theme';
import {
  DownloadNameInput,
  SaveDownloadDialogContainer,
  SaveDownloadDialogTitle,
} from 'spw-programming-projects/downloads/dialogs/styles';
import { Box, FormHelperText } from '@mui/material';
import { DialogForm } from 'spw-components/dialogs/styles';
import styled from 'styled-components';

export const DownloadButton = styled(SPWMainButton)`
  width: 113px;
`;

export const DownloadNameCardActions = styled.div`
  > :first-child {
    margin-right: 16px;
  }
`;

type Props = {
  onCancel: () => void;
  onSubmit: (name: string) => void;
  initialName?: string;
  maxLength?: number;
  errorMessage?: string;
};

const NameDownloadDialog = ({
  onCancel,
  onSubmit,
  initialName = '',
  maxLength = 50,
  errorMessage,
}: Props) => {
  const [downloadName, setDownloadName] = useState(initialName);
  const [touched, setTouched] = useState(false);

  const emptyName = useMemo(() => /^[\s]*$/.test(downloadName), [downloadName]);

  const handleDownloadNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setDownloadName(e.target.value);
      setTouched(true);
    },
    [setDownloadName, setTouched]
  );

  const handleKeyPressed = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && !emptyName) {
        onSubmit(downloadName);
      }
    },
    [onSubmit, downloadName, emptyName]
  );

  const handleSubmitButtonClick = useCallback(() => {
    onSubmit(downloadName);
  }, [onSubmit, downloadName]);

  const handleCancelButtonClick = useCallback(() => {
    onCancel();
  }, [onCancel]);
  const showError = emptyName && touched;

  return (
    <SaveDownloadDialogContainer>
      <SaveDownloadDialogTitle>Download Name</SaveDownloadDialogTitle>
      <Box>
        <DialogForm>
          <FormBox>
            <DownloadNameInput
              data-cy="download-name-input"
              autoFocus
              fullWidth
              value={downloadName}
              onChange={handleDownloadNameChange}
              onKeyPress={handleKeyPressed}
              inputProps={{ maxLength }}
              variant="outlined"
            />
            <Box height={20}>
              <FormHelperText error>
                {showError &&
                  `${errorMessage || 'A download name is required.'}`}
              </FormHelperText>
            </Box>
          </FormBox>
        </DialogForm>
      </Box>
      <DownloadNameCardActions>
        <DownloadButton
          onClick={handleSubmitButtonClick}
          disabled={emptyName}
          data-cy="download-button"
        >
          Download
        </DownloadButton>
        <SPWCancelButton
          onClick={handleCancelButtonClick}
          customColor={colors.black}
        />
      </DownloadNameCardActions>
    </SaveDownloadDialogContainer>
  );
};

export default NameDownloadDialog;
